import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

/**  Custom components stores services types constant */
import { ProductType, LogoRequestDetails, RequestMetadaContext, LogoRequestConstants } from "../../features/LogoRequest/logoRequest.Type";
import { documentUploadService, ShareFileFolderPath } from "../../features/LogoRequest/documentUploadService";
import { logoRequestService } from "../../features/LogoRequest/logoRequestService";
import { STATUS } from "../../utils/statusHandler";
import Loader from "../../components/Loader/Loader";
import ErrorMessage from "../../components/Error-Message/Error-Message";
import ProductLogoRequestDetails from "../../features/LogoRequest/ProductLogoRequestDetails/ProductLogoRequestDetails";
import { CERTIFICATE_STATUS } from "../../features/Certificates/certificates.types";
import { RootState } from "../../store/store";

/** Styles */
import "./logoRequest.style.css";

const LogoRequest = () => {
  const { type, requestId, certificateId } = useParams<{ type: string; requestId: string, certificateId: string }>();
  const [loadingRequestDetailData, setLoadingRequestDetailData] = useState(true);
  const [errorRequestDetailData, setErrorRequestDetailData] = useState<string | null>(null);
  const [requestDetailData, setRequestDetailData] = useState<LogoRequestDetails>();
  const numericType = Number(type);
  const requestIdVal = (requestId) ? requestId : "";
  const loginUserData = useSelector((state: RootState) => state.users.loginUser);

  useEffect(() => {
    if (requestId) {
      getLogoRequestDetails(requestId);
    }

    /* const data = location.state?.responseData;
     if(data){
       setRequestDetailData(data as LogoRequestDetails);
       setLoadingRequestDetailData(false);
       navigate(location.pathname, { replace: true, state: null });
     }else if(requestId){
       getLogoRequestDetails(requestId);
     }*/
  }, []);


  /**
   * Get logo request details
   * @param logoRequestId 
   */
  const getLogoRequestDetails = async (logoRequestId: string) => {
    try {
      const requestDetailResponse = await logoRequestService.fetchLogoRequestDetails(logoRequestId);
      if (requestDetailResponse.status == STATUS.DRAFT || requestDetailResponse.status == STATUS.CHANGE_REQUESTED) {
        let tempCertificateId = certificateId || "";
        const customerSites = await logoRequestService.fetchCustomerSites(tempCertificateId);
        if (customerSites?.length > 0) {
          requestDetailResponse.requestDetailsData.allSiteMetaData = customerSites;
        }
      }
      setRequestDetailData(requestDetailResponse as LogoRequestDetails);
      // If share file path is not present then create it first
      if((!requestDetailResponse.agreementShareFilePath || requestDetailResponse.agreementShareFilePath == "") || (!requestDetailResponse.documentShareFilePath || requestDetailResponse.documentShareFilePath == "")){
        createShareFileFolders(requestDetailResponse);
      }
    } catch (err: any) {
      setErrorRequestDetailData(err.message);
    } finally {
      setLoadingRequestDetailData(false);
    }
  }

  /**
   * Update sharefilepath
   * @param data 
   */
  const updateShareFilePath = async (data: LogoRequestDetails) => {
    try {
      let statusToUpdate = data.status;
      let result = await logoRequestService.updateLogoRequest(data,statusToUpdate, "");
      if (!result.isSuccess) {
        let errorMessage = result?.message || LogoRequestConstants.SHAREFFILEPATH_UPDATE_FIL_MSG;
        toast.error(errorMessage);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || LogoRequestConstants.SHAREFFILEPATH_UPDATE_FIL_MSG;
      toast.error(errorMessage);
    }
  }

    /**
   * Create share file folders
   * @param responseData 
   */
  const createShareFileFolders = async (responseData: LogoRequestDetails) => {
    try {
      const folderPathRes: ShareFileFolderPath = await documentUploadService.createFoldersInEgnyte(
        responseData.customFolderPath,
        loginUserData
      );

      if (process.env.REACT_APP_EGNYTEPATH_PREFIX) {
        // Build raw URLs
        let labelingAgrementPath =
          process.env.REACT_APP_EGNYTEPATH_PREFIX +
          "/" +
          responseData.customFolderPath +
          "/LabelingAgreement";

        let documentFolederPath =
          process.env.REACT_APP_EGNYTEPATH_PREFIX +
          "/" +
          responseData.customFolderPath;

        // Replace spaces with %20
        labelingAgrementPath = labelingAgrementPath.replace(/ /g, "%20");
        documentFolederPath = documentFolederPath.replace(/ /g, "%20");

        // Wrap them as HTML anchor tags
        const labelingAgreementAnchor = `<a href="${labelingAgrementPath}" target="_blank">${labelingAgrementPath}</a>`;
        const documentFolderAnchor = `<a href="${documentFolederPath}" target="_blank">${documentFolederPath}</a>`;

        // Store in state (now contains the <a> tag string)
        setRequestDetailData(prev => ({
          ...prev!,
          agreementShareFilePath: labelingAgreementAnchor,
          documentShareFilePath: documentFolderAnchor
        }));

        // Send to backend
        responseData.agreementShareFilePath = labelingAgreementAnchor;
        responseData.documentShareFilePath = documentFolderAnchor;

        updateShareFilePath(responseData);
      } else {
        console.log("Error - Egnyte prefix path not found !!");
      }
    } catch (exe) {
      console.log(exe);
    }
  }

  // Display loader while fetching data 
  if (loadingRequestDetailData) {
    return <Loader />;
  }

  // Display Error while fetching data 
  if (errorRequestDetailData != null) {
    return <ErrorMessage message={errorRequestDetailData} />
  }

  /**
  * Check is messageing enabled or not
  * @returns 
  */
  const isMessageEnabled = () => {
    let value = true;
    if (requestDetailData?.status == STATUS.APPROVED || requestDetailData?.status == STATUS.CANCELLED) {
      value = false
    }
    // If certificate status is not certified then logo request should display in read only mode
    if(requestDetailData?.certificate?.status != CERTIFICATE_STATUS.CERTIFIED){
      value = false
    }
    return value;
  }

  return (
    <>
      <main className="container-fluid dashboard-bg">
        <div className="main main-app p-3 p-lg-4">
          <RequestMetadaContext.Provider value={{ requestId: requestIdVal, isReadOnly: requestDetailData?.isReadOnly ? requestDetailData?.isReadOnly : false, requestType: requestDetailData?.requestType || '', status: requestDetailData?.status || '', isMessageEnabled: isMessageEnabled(), isDocumentUploadEnabled: isMessageEnabled(), logoRequestName: requestDetailData?.requestName || "" }}>
            {(numericType === ProductType.OnProduct || numericType === ProductType.OffProduct) && requestDetailData && (
              <ProductLogoRequestDetails initialData={requestDetailData} />
            )}
          </RequestMetadaContext.Provider>
        </div>
      </main>
    </>
  );
};

export default LogoRequest;
